<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <table class="table-fixed">
                <thead class="sticky z-10 top-0 bg-white">
                    <tr>
                        <th style="width: 3.5rem" class="text-left border-r p-1">
                            {{ '#' }}
                        </th>
                        <th class="text-left border-r p-1 bg-white">
                            <span>{{ $t('project.follow.observation.titleLabel') }}</span>
                        </th>
                        <th style="" class="text-left border-r p-1 hidden sm:table-cell">
                            {{ $t('project.follow.observation.recipients') }}
                        </th>
                        <th class="text-center border-r p-1 hidden sm:table-cell" style="width: 6rem">
                            {{ $t('project.follow.observation.resolvedAt') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="observation in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1">
                                <div
                                    class="p-1 px-2 border flex justify-center"
                                    :class="{
                                        [getObservationClass(observation)]: true,
                                    }"
                                >
                                    {{ (observation.index + 1).toString().padStart(3, '0') }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followSynthesis',
                                        params: {
                                            ...$route.params,
                                            observationId: observation.id,
                                        },
                                    }"
                                >
                                    <app-clamp :htmlContent="observation.title" :number-of-lines="2" />
                                    <span v-if="!observation.title || observation.title.trim().length === 0">...</span>
                                </router-link>
                            </td>
                            <td class="border-r p-1 truncate hidden sm:table-cell">
                                <div
                                    v-if="observation.recipients.length === 1"
                                    style="max-width: 10rem"
                                    class="truncate"
                                >
                                    <span class="">
                                        {{
                                            observation.recipients[0].reference
                                                ? '#' +
                                                  observation.recipients[0].reference +
                                                  ' ' +
                                                  observation.recipients[0].name
                                                : observation.recipients[0].name
                                        }}
                                    </span>
                                </div>
                                <div class="flex flex-col w-full" v-if="observation.recipients.length === 2">
                                    <span>
                                        {{
                                            (observation.recipients[0].reference || '') +
                                            ' ' +
                                            observation.recipients[0].name
                                        }}
                                    </span>
                                    <span class="">
                                        {{
                                            (observation.recipients[1].reference || '') +
                                            ' ' +
                                            observation.recipients[1].name
                                        }}
                                    </span>
                                </div>
                                <span
                                    v-if="observation.recipients.length >= 3"
                                    :title="
                                        observation.recipients
                                            .map((recipient) => (recipient.reference || '') + ' ' + recipient.name)
                                            .join('\n')
                                    "
                                >
                                    {{ observation.recipients.map((recipient) => recipient.reference || '').join() }}
                                </span>
                            </td>
                            <td class="text-center border-r p-1 hidden sm:table-cell">
                                {{ observation.resolvedAt | humanizeDate }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { filterMatch, sortBy } from '@/services/sanitize.service';
import { getAllObservationsSyntheses } from '../observations/observation.service';
import { getObservationClass } from '@/features/observations/observation.service';
import { combineLatest } from 'rxjs';
import { getBundleMap } from '@/features/bundles/bundles.service';
import AppClamp from '@/components/appClamp/AppClamp.vue';
import { getSynthesesExport } from '@/features/syntheses/SynthesesExports.service';

export default {
    components: { AppClamp },
    async created() {
        this.restoreFilter();
        this.init();
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'resolvedAt') {
                        return item.resolvedAt;
                    } else {
                        return item.index;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getAllObservationsSyntheses(this.$route.params.projectId),
                    getBundleMap(this.$route.params.projectId),
                ]).subscribe(async ([observations, bundleMap]) => {
                    this.bundles = sortBy(Object.values(bundleMap), (bundle) => `${bundle.reference}${bundle.name}`);
                    this.items = observations.map((observation) => {
                        const recipients = observation.recipientIds.map((id) => bundleMap[id]);
                        return {
                            ...observation,
                            recipients,
                            filterString: [
                                observation.index + 1,
                                observation.title,
                                recipients.map((recipient) => recipient.reference + ' ' + recipient.name).join(),
                            ].join(),
                        };
                    });
                }),
            ];
        },
        getObservationClass,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveSort();
        },
        matchBundleFilter(item, bundleIds) {
            return item.recipientIds && item.recipientIds.find((id) => bundleIds.includes(id));
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.title, criteria, true));
        },
        matchStatusFilter(item) {
            const isResolved = item.resolvedAt && !item.validatedAt;
            const isObsolete = !!item.obsoleteAt;
            const isValidated = !!item.validatedAt;
            const isPending = !item.resolvedAt;
            return (
                isObsolete === this.obsolete &&
                ((isResolved && this.resolved) || (isValidated && this.validated) || (isPending && this.pending))
            );
        },
        async restoreFilter() {
            const synthesesExport = await getSynthesesExport(
                this.$route.params.projectId,
                this.$route.params.synthesesExportId,
            );
            if (synthesesExport) {
                this.filterValue = synthesesExport.filter || [];
                this.sortKey = synthesesExport.sortKey || 'object';
                this.obsolete = !!synthesesExport.obsolete;
                this.resolved = !!synthesesExport.resolved;
                this.validated = !!synthesesExport.validated;
                this.pending = !!synthesesExport.pending;
            }
        },
        filterFn(filter) {
            const bundleIds = filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id);
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchStatusFilter: this.matchStatusFilter(item),
                    matchString: this.matchString(stringCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
    },
    data() {
        return {
            bundles: [],
            subscriptions: [],
            sortKey: 'name',
            sortAsc: true,
            readOnly: true,
            items: [],
            resolved: false,
            validated: true,
            pending: false,
            obsolete: false,
            filterValue: [],
        };
    },
};
</script>
